import React, { useCallback, useRef, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export interface FileUploaderProps {
  selectedFile: File | null;
  fileName: string;
  uploading: boolean;
  previewUrl?: string | null;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  accept?: Record<string, string[]>;
  maxSize?: number;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  selectedFile,
  fileName,
  uploading,
  previewUrl,
  disabled = false,
  onFileSelect,
  onFileRemove,
  accept,
  maxSize,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile && !previewUrl) {
      const fileExtension =
        selectedFile.name.split(".").pop()?.toLowerCase() || "";
      const imageExtensions = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "bmp",
        "webp",
        "svg",
      ];

      if (imageExtensions.includes(fileExtension)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLocalPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setLocalPreviewUrl(null);
      }
    } else if (!selectedFile) {
      setLocalPreviewUrl(null);
    } else {
      setLocalPreviewUrl(previewUrl || null);
    }
  }, [selectedFile, previewUrl]);

  const getFileIconAndColor = (
    fileName: string,
  ): { icon: React.ReactElement; color: string } => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    if (["pdf"].includes(extension)) {
      return {
        icon: <PictureAsPdfIcon sx={{ fontSize: 48, color: "#d32f2f" }} />,
        color: "#d32f2f",
      };
    }
    if (["xls", "xlsx", "csv"].includes(extension)) {
      return {
        icon: <TableChartIcon sx={{ fontSize: 48, color: "#2e7d32" }} />,
        color: "#2e7d32",
      };
    }
    if (["ppt", "pptx"].includes(extension)) {
      return {
        icon: <SlideshowIcon sx={{ fontSize: 48, color: "#ed6c02" }} />,
        color: "#ed6c02",
      };
    }
    if (["doc", "docx", "txt", "rtf"].includes(extension)) {
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: "#1976d2" }} />,
        color: "#1976d2",
      };
    }
    return {
      icon: <InsertDriveFileIcon sx={{ fontSize: 48, color: "#99A2AD" }} />,
      color: "#99A2AD",
    };
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      onFileSelect(acceptedFiles[0]);
    },
    [onFileSelect],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploading || disabled,
    accept,
    maxSize,
  });

  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed #aaa",
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
        textAlign: "center",
        cursor: uploading || disabled ? "not-allowed" : "pointer",
        bgcolor:
          uploading || disabled
            ? "action.disabledBackground"
            : isDragActive
              ? "action.hover"
              : "background.paper",
        minHeight: { xs: 120, sm: 150 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
        transition: "background-color 0.2s",
      }}
      aria-label={t("attachmentForm.dropzoneLabel")}
    >
      <input {...getInputProps()} ref={fileInputRef} />
      {uploading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={24} />
          <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            {t("attachmentForm.uploading")}
          </Typography>
        </Box>
      ) : selectedFile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          {localPreviewUrl ? (
            <Box
              sx={{
                width: 120,
                height: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Box
                component="img"
                src={localPreviewUrl}
                alt={fileName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          ) : (
            (() => {
              const { icon, color } = getFileIconAndColor(fileName);
              return (
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid",
                    borderColor: color,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  {icon}
                </Box>
              );
            })()
          )}
          <Typography
            sx={{
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              textAlign: "center",
              wordBreak: "break-word",
              maxWidth: "100%",
            }}
          >
            {fileName}
          </Typography>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onFileRemove();
            }}
            startIcon={<CloseIcon />}
            color="error"
            disabled={uploading || disabled}
          >
            {t("attachmentForm.removeFile")}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: "#99A2AD" }} />
          <Typography
            sx={{
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              fontWeight: "bold",
            }}
          >
            {t("attachmentForm.dropzoneText")}
          </Typography>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectFile();
            }}
            variant="outlined"
            disabled={uploading || disabled}
          >
            {t("attachmentForm.selectFileButton")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FileUploader;
